import React, { useState } from 'react';
import { ArrowUpRight, Sparkles, MapPin, Briefcase, Search, Filter } from 'lucide-react';
import Reveal from '../components/Reveal';
import SectionHeader from '../components/SectionHeader';
import { JOBS } from '../mock/data';
import { toast } from 'sonner';

const TYPES = ['All', 'Contract', 'C2H', 'Direct Hire', 'Executive Search'];

const Careers = () => {
  const [q, setQ] = useState('');
  const [type, setType] = useState('All');

  const filtered = JOBS.filter(j => {
    const matchesQ = (j.title + ' ' + j.location + ' ' + j.tags.join(' ')).toLowerCase().includes(q.toLowerCase());
    const matchesT = type === 'All' || j.type === type;
    return matchesQ && matchesT;
  });

  return (
    <>
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="hero-glow" />
        <div className="container-x relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Careers
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl md:text-6xl font-bold text-white leading-[1.05] max-w-4xl">
              Find your next role in <span className="shimmer-text">tech.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-white/60 text-lg max-w-2xl mt-6">
              Browse open engineering, data, cloud and leadership roles — vetted by us, hiring at some of the most exciting US technology companies.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-12">
        <div className="container-x">
          <div className="glass rounded-2xl p-5 flex flex-col md:flex-row gap-3">
            <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
              <Search className="w-4 h-4 text-white/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by role, skill or location…"
                className="bg-transparent outline-none flex-1 text-sm text-white placeholder-white/40"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-white/40 hidden sm:block" />
              {TYPES.map(t => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`text-xs px-3 py-2 rounded-full border transition-colors ${type === t ? 'bg-cyan-400 border-cyan-400 text-[#05070E] font-medium' : 'border-white/10 text-white/70 hover:border-cyan-400/40 hover:text-white'}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          {filtered.length === 0 ? (
            <div className="glass rounded-2xl p-14 text-center">
              <p className="text-white/60">No roles match your search. Try broadening your filters.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filtered.map((j, i) => (
                <Reveal key={i} delay={i * 60}>
                  <div className="tilt-card glass rounded-2xl p-6 group h-full flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-cyan-300 mb-2">{j.type}</div>
                        <h3 className="font-display text-xl font-semibold text-white group-hover:text-cyan-300 transition-colors">{j.title}</h3>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300 shrink-0">
                        <Briefcase className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 text-sm text-white/60">
                      <MapPin className="w-4 h-4" /> {j.location}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {j.tags.map((t, k) => (
                        <span key={k} className="px-2.5 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-white/70">{t}</span>
                      ))}
                    </div>
                    <button
                      onClick={() => toast.success(`Application started for ${j.title}`, { description: 'We\u2019ll be in touch within 48 hours.' })}
                      className="btn-outline inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-xs mt-6 self-start"
                    >
                      Apply now <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Careers;
